import { API_BASE_URL } from "../../core/apiClient.ts";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Page } from "./types.ts";
export interface FieldError {
  path: string;
  message: string;
}
export class TriggerApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fieldErrors: FieldError[] = [],
  ) {
    super(message);
  }
}
export async function request<T>(
  path: string,
  method = "GET",
  body?: unknown,
  key?: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(API_BASE_URL + "/api" + path, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(key ? { "Idempotency-Key": key } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data: any;
  try {
    data = await response.json();
  } catch {
    throw new TriggerApiError(
      response.status,
      "UNAVAILABLE",
      "Не вдалося прочитати відповідь сервера",
    );
  }
  if (!response.ok)
    throw new TriggerApiError(
      response.status,
      data.code ?? "UNAVAILABLE",
      data.message ?? data.error ?? "Не вдалося виконати запит",
      data.fieldErrors ?? [],
    );
  return data;
}
export function errorMessage(error: unknown) {
  if (error instanceof TriggerApiError) {
    if(error.code==='VALIDATION' && /[А-Яа-яІіЇїЄє]/.test(error.message))return error.message;
    if (error.code === "PREVIEW_CHANGED")
      return "Preview застарів. Почніть перевірку з першої сторінки.";
    if (error.code === "QUEUE_PAUSED" || error.code === "FEATURE_DISABLED")
      return "Чергу або Meta-дії вимкнено. Перевірте стан черги.";
    if (error.code === "STALE_DECISION")
      return "Докази застаріли. Синхронізуйте повні дані кабінетів перед повтором.";
    if (error.code === "NOT_RETRYABLE" || error.code === "NOT_CANCELLABLE")
      return "Цей результат уже не допускає обрану дію. Оновіть результати.";
    if (error.status === 409)
      return "Дані змінилися на сервері. Ваші зміни збережені у формі. Перечитайте поточну версію перед повторним збереженням.";
    const messages: Record<string, string> = {
      VALIDATION:
        "Перевірте правило, сумісність списків та активні призначення.",
      NOT_FOUND: "Об’єкт більше не доступний. Перечитайте поточні дані.",
      UNAVAILABLE: "Сервіс тимчасово недоступний. Спробуйте ще раз.",
      OWNERSHIP: "Об’єкт недоступний у вибраному кабінеті.",
      LAST_ASSIGNMENT:
        "Спершу вимкніть тригер, щоб прибрати останнє активне призначення.",
      SCOPE_IN_USE:
        "Список використовується активними призначеннями. Спершу деактивуйте їх.",
      PREVIEW_CHANGED: "Preview застарів. Почніть перевірку з першої сторінки.",
    };
    return messages[error.code] ?? error.message;
  }
  if (error instanceof TypeError)
    return "Не вдалося з’єднатися із сервером. Перевірте з’єднання та повторіть запит.";
  return error instanceof Error ? error.message : "Не вдалося виконати запит";
}
export async function allPages<T>(
  path: string,
  signal?: AbortSignal,
): Promise<T[]> {
  let cursor: string | null = "",
    items: T[] = [];
  do {
    const page: Page<T> = await request<Page<T>>(
      path +
        (path.includes("?") ? "&" : "?") +
        "limit=100&cursor=" +
        encodeURIComponent(cursor ?? ""),
      "GET",
      undefined,
      undefined,
      signal,
    );
    items.push(...page.items);
    cursor = page.nextCursor;
    if (items.length > 5000)
      throw Error("Забагато даних. Звузьте область пошуку.");
  } while (cursor);
  return items;
}
export function usePage<T>(path: string | null) {
  const [items, setItems] = useState<T[]>([]),
    [cursor, setCursor] = useState<string | null>(null),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [revision, setRevision] = useState(0);
  const generation = useRef(0),
    busy = useRef(false),
    abort = useRef<AbortController>();
  const load = useCallback(
    async (next: string | null = null) => {
      if (!path || busy.current) return;
      busy.current = true;
      setLoading(true);
      setError("");
      const current = ++generation.current;
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      try {
        const page = await request<Page<T>>(
          path +
            (path.includes("?") ? "&" : "?") +
            "limit=50" +
            (next ? "&cursor=" + encodeURIComponent(next) : ""),
          "GET",
          undefined,
          undefined,
          controller.signal,
        );
        if (current === generation.current) {
          setItems((previous) =>
            next ? [...previous, ...page.items] : page.items,
          );
          setCursor(page.nextCursor);
        }
      } catch (e) {
        if (current === generation.current && !controller.signal.aborted)
          setError(errorMessage(e));
      } finally {
        if (current === generation.current) {
          busy.current = false;
          setLoading(false);
        }
      }
    },
    [path],
  );
  useEffect(() => {
    abort.current?.abort();
    generation.current++;
    busy.current = false;
    setItems([]);
    setCursor(null);
    setError("");
    setLoading(false);
    void load();
    return () => {
      abort.current?.abort();
      generation.current++;
      busy.current = false;
    };
  }, [load, revision]);
  return {
    items,
    cursor,
    loading,
    error,
    more: () => load(cursor),
    refresh: () => setRevision((x) => x + 1),
  };
}
